import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { API_URL } from "../../config";
import "./css/SalespeopleReport.css";

const SALES_VISIT_SUMMARY_APIS = [
    `${API_URL}/api/get-sales-visit-summary`,
    `${API_URL}/get-sales-visit-summary`,
];
const RECENT_SALES_VISIT_APIS = [
    `${API_URL}/api/get-sales-customer-visit-reports`,
    `${API_URL}/api/get-recent-sales-visits`,
    `${API_URL}/get-recent-sales-visits`,
];
const TARGET_VISITS = 50;

function SalespeopleReport({ authInfo }) {
    const [summaryRows, setSummaryRows] = useState([]);
    const [recentVisits, setRecentVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedRow, setSelectedRow] = useState(null);

    useEffect(() => {
        const headers = authInfo?.token
            ? {
                  "auth-token": authInfo.token,
                  Authorization: `Bearer ${authInfo.token}`,
              }
            : {};

        const run = async () => {
            setLoading(true);
            setError("");
            try {
                const [summaryRes, visitsRes] = await Promise.all([
                    fetchSalesVisitSummaryData(headers),
                    fetchRecentSalesVisitsData(headers, 300),
                ]);
                setSummaryRows(extractSummaryRows(summaryRes.data));
                setRecentVisits(extractRecentVisits(visitsRes.data));
            } catch (err) {
                setSummaryRows([]);
                setRecentVisits([]);
                setError(err?.message || "Failed to load salesperson performance.");
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [authInfo?.token]);

    const rows = useMemo(() => {
        return summaryRows
            .map((row) => {
                const totalVisits = Number(row.total_visits || 0);
                const completed = Number(row.completed_count || 0);
                const upcoming = Number(row.upcoming_followups || 0);
                const percent = Math.round((Math.min(completed, TARGET_VISITS) / TARGET_VISITS) * 100);
                return {
                    ...row,
                    totalVisits,
                    completed,
                    upcoming,
                    percent,
                };
            })
            .sort((a, b) => b.percent - a.percent);
    }, [summaryRows]);

    return (
        <div className="salesPeopleWrap">
            <div className="spHead">
                <h1>Salespeople Performance Management</h1>
                <p>Welcome back! Here&apos;s what&apos;s happening today.</p>
            </div>
            <div className="salesPeopleCard">
                <h3>Salesperson Performance</h3>

                {error ? <div className="spError">{error}</div> : null}
                {loading ? (
                    <p className="spEmpty">Loading...</p>
                ) : rows.length === 0 ? (
                    <p className="spEmpty">No salesperson data found.</p>
                ) : (
                    <div className="spTableWrap">
                        <table className="spTable">
                            <thead>
                                <tr>
                                    <th>Salesperson</th>
                                    <th>Total Visits</th>
                                    <th>Upcoming Follow-Ups</th>
                                    <th>Performance</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, idx) => (
                                    <tr key={`sp-row-${row.sales_person_id || idx}`}>
                                        <td>{row.sales_person_name || "-"}</td>
                                        <td>{row.totalVisits}</td>
                                        <td>{row.upcoming}</td>
                                        <td>
                                            <div className="spPerfText">
                                                <span>{row.completed} / {TARGET_VISITS} visits</span>
                                                <strong>{row.percent}%</strong>
                                            </div>
                                            <div className="spPerfBar">
                                                <span
                                                    className={`spPerfFill ${row.percent >= 85 ? "good" : row.percent >= 70 ? "mid" : "low"}`}
                                                    style={{ width: `${Math.max(6, row.percent)}%` }}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="spReportBtn"
                                                onClick={() => setSelectedRow(row)}
                                            >
                                                Report
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selectedRow ? (
                <ReportModal
                    row={selectedRow}
                    visits={recentVisits}
                    onClose={() => setSelectedRow(null)}
                />
            ) : null}
        </div>
    );
}

function ReportModal({ row, visits, onClose }) {
    const monthsData = useMemo(() => {
        const now = new Date();
        const months = [];
        for (let i = 2; i >= 0; i -= 1) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.push({
                key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
                label: d.toLocaleString("en-US", { month: "short" }),
                count: 0,
            });
        }

        visits.forEach((item) => {
            const itemDate = new Date(item?.visit_date || "");
            if (Number.isNaN(itemDate.getTime())) return;

            const samePersonById =
                String(item.sales_person_id || "") === String(row.sales_person_id || "");
            const samePersonByName =
                (item.sales_person_name || "").trim() === (row.sales_person_name || "").trim();
            if (!samePersonById && !samePersonByName) return;

            const key = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, "0")}`;
            const monthRow = months.find((m) => m.key === key);
            if (monthRow) monthRow.count += 1;
        });

        return months;
    }, [visits, row.sales_person_id, row.sales_person_name]);

    const maxCount = Math.max(20, ...monthsData.map((m) => m.count));
    const achievements = buildAchievements(row);

    return (
        <div className="spModalOverlay" onClick={onClose}>
            <div className="spModalCard" onClick={(e) => e.stopPropagation()}>
                <div className="spModalHead">
                    <h3>Performance Report - {row.sales_person_name || "-"}</h3>
                    <button type="button" className="spModalClose" onClick={onClose}>×</button>
                </div>

                <div className="spStatsGrid">
                    <div className="spStatBox statTotal">
                        <p>Total Visits</p>
                        <h4>{row.totalVisits}</h4>
                    </div>
                    <div className="spStatBox statCompleted">
                        <p>Completed</p>
                        <h4>{row.completed}</h4>
                    </div>
                    <div className="spStatBox statFollow">
                        <p>Follow-Ups</p>
                        <h4>{row.upcoming}</h4>
                    </div>
                </div>

                <div className="spSectionTitle">Overall Performance</div>
                <div className="spPerfText modalPerf">
                    <span>{row.completed} / {TARGET_VISITS} visits</span>
                    <strong>{row.percent}%</strong>
                </div>
                <div className="spPerfBar large">
                    <span
                        className={`spPerfFill ${row.percent >= 85 ? "good" : row.percent >= 70 ? "mid" : "low"}`}
                        style={{ width: `${Math.max(6, row.percent)}%` }}
                    />
                </div>

                <div className="spSectionTitle">Monthly Performance</div>
                <div className="spChartWrap">
                    {monthsData.map((m) => (
                        <div className="spChartCol" key={m.key}>
                            <div className="spChartBarBg">
                                <span
                                    className="spChartBar"
                                    style={{ height: `${(m.count / maxCount) * 100}%` }}
                                />
                            </div>
                            <div className="spChartLabel">{m.label}</div>
                        </div>
                    ))}
                </div>

                <div className="spSectionTitle">Recent Achievements</div>
                <ul className="spAchievements">
                    {achievements.map((item, idx) => (
                        <li key={`sp-ach-${idx}`}>{item}</li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function buildAchievements(row) {
    const totalVisits = Number(row.totalVisits || 0);
    const upcoming = Number(row.upcoming || 0);
    const overdue = Number(row.overdue_followups || 0);
    const percent = Number(row.percent || 0);
    const list = [];

    if (totalVisits > TARGET_VISITS) {
        const extra = Math.round(((totalVisits - TARGET_VISITS) / TARGET_VISITS) * 100);
        list.push(`Exceeded monthly target by ${extra}%`);
    } else {
        list.push(`Achieved ${percent}% of monthly target`);
    }
    list.push(`Handled ${upcoming} upcoming follow-up${upcoming === 1 ? "" : "s"} proactively`);
    if (overdue === 0) {
        list.push("Zero overdue follow-ups this month");
    } else {
        list.push(`Reduced overdue follow-ups to ${overdue}`);
    }
    return list;
}

function extractSummaryRows(payload) {
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
}

function extractRecentVisits(payload) {
    const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.message)
            ? payload.message
            : Array.isArray(payload?.result)
              ? payload.result
              : [];
    return rows.filter((row) => row && (row.visit_date || row.customer_name));
}

async function fetchSalesVisitSummaryData(headers) {
    let lastError = null;
    for (const url of SALES_VISIT_SUMMARY_APIS) {
        try {
            return await axios.get(url, { headers });
        } catch (err) {
            lastError = err;
        }
    }
    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load sales visit summary."
    );
}

async function fetchRecentSalesVisitsData(headers, limit = 300) {
    let lastError = null;
    for (const url of RECENT_SALES_VISIT_APIS) {
        try {
            return await axios.get(url, { headers, params: { include_all: 1, limit } });
        } catch (errGet) {
            lastError = errGet;
        }
        try {
            return await axios.post(url, { include_all: 1, limit }, { headers });
        } catch (errPost) {
            lastError = errPost;
        }
    }
    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load recent sales visits."
    );
}

const mapStateToProps = (state) => ({
    authInfo: state.authInfoReducer,
});

export default connect(mapStateToProps)(SalespeopleReport);
